/**
 * @module engine3d/solid/presets
 * @summary Ready-made parts: a hopper, a feeder, a silo, a chute and the rest,
 *   each as a feature stack the user can then take apart.
 * @description
 *   These are the reason the modeller is usable in the first minute. Nobody
 *   wants to build a hopper out of five primitives before finding out whether
 *   the tool is any good — so every template lands as a NAMED, EDITABLE stack:
 *   pick "Square hopper", get the four plates, the outlet spout, the top rim and
 *   the legs as separate tree rows, then change the valley angle by dragging one
 *   number.
 *
 *   Every dimension below is in millimetres and is roughly what the real item
 *   would be, so the templates read as engineering rather than as sample data.
 *
 *   // INVARIANT: a template only ever returns fresh `Feature` objects; nothing
 *   // here is shared between two parts.
 */
import { mkFeature, type Feature, type FeatureKind, type Part } from './part';
import type { MatKey } from '../materials';

/** Shorthand for a template row: kind, name, params, placement. */
function ft(
  kind: FeatureKind,
  name: string,
  p: Record<string, number>,
  place: Partial<Feature> = {},
): Feature {
  return mkFeature(kind, { name, p, ...place });
}

/** Legs on a rectangular pitch — the 2 x 2 pattern most equipment stands on. */
function legs(
  w: number,
  d: number,
  h: number,
  sec = 90,
  mat: MatKey = 'column',
  name = 'Legs',
): Feature {
  return ft(
    'box',
    name,
    { w: sec, h, d: sec },
    {
      mat,
      x: -w / 2,
      z: -d / 2,
      pattern: { kind: 'linear', count: 2, spacing: w, axis: 'x', count2: 2, spacing2: d, axis2: 'z' },
    },
  );
}

export interface Template {
  id: string;
  label: string;
  icon: string;
  blurb: string;
  build: () => Feature[];
}

/**
 * Side elevation of a straight stair: a stepped top edge over a straight
 * soffit, ready to extrude across the going. Rise and going are the standard
 * industrial pitch — 245 up, 272 out, which lands at about 42 degrees.
 */
function stairProfile(rise: number, going: number, steps: number): { x: number; y: number }[] {
  const total = rise * steps;
  const pts: { x: number; y: number }[] = [{ x: 0, y: total }];
  for (let i = 0; i < steps; i++) {
    pts.push({ x: (i + 1) * going, y: total - i * rise });
    pts.push({ x: (i + 1) * going, y: total - (i + 1) * rise });
  }
  // Back along the soffit, kept clear of the nosings the whole way.
  pts.push({ x: 0, y: total - rise * 1.2 });
  return pts;
}

const STAIR_PROFILE = stairProfile(245, 272, 13);

export const TEMPLATES: Template[] = [
  {
    id: 'hopper-sq',
    label: 'Square hopper',
    icon: '⧨',
    blurb: 'Four-plate hopper on legs, with rim and outlet spout',
    build: () => [
      legs(1500, 1500, 1200),
      ft('box', 'Base frame', { w: 1680, h: 120, d: 140 }, {
        mat: 'column',
        y: 1200,
        z: -770,
        pattern: { kind: 'linear', count: 2, spacing: 1540, axis: 'z' },
      }),
      ft('loft', 'Hopper plates', { w0: 300, d0: 300, w1: 1600, d1: 1600, h: 1150 }, { y: 1320 }),
      ft('box', 'Top rim', { w: 1680, h: 70, d: 1680 }, { mat: 'steel', y: 2440 }),
      ft('pipe', 'Outlet spout', { r: 150, len: 320, flange: 1, seg: 24 }, { y: 1000 }),
      ft('box', 'Slide gate', { w: 460, h: 30, d: 380 }, { mat: 'machine', y: 990 }),
    ],
  },
  {
    id: 'hopper-round',
    label: 'Conical hopper',
    icon: '▽',
    blurb: 'Rolled cone under a cylindrical section, on three legs',
    build: () => [
      ft('box', 'Legs', { w: 100, h: 1300, d: 100 }, {
        mat: 'column',
        pattern: { kind: 'circular', count: 3, spacing: 700, axis: 'y', sweep: 360 },
      }),
      ft('cone', 'Hopper cone', { r0: 130, r1: 800, h: 1000, seg: 32 }, { y: 1300 }),
      ft('cyl', 'Shell', { r: 800, h: 900, seg: 32 }, { mat: 'galv', y: 2300 }),
      ft('ring', 'Top stiffener', { R: 810, r: 26, seg: 36 }, { mat: 'steel', y: 3200 }),
      ft('ring', 'Cone joint ring', { R: 810, r: 22, seg: 36 }, { mat: 'steel', y: 2300 }),
      ft('pipe', 'Outlet', { r: 150, len: 300, flange: 1, seg: 24 }, { y: 1010 }),
    ],
  },
  {
    id: 'feeder-vib',
    label: 'Vibrating feeder',
    icon: '⇉',
    blurb: 'Pan feeder: trough, side walls, drive and spring mounts',
    build: () => [
      legs(1300, 700, 800, 90, 'column', 'Support legs'),
      ft('cyl', 'Springs', { r: 55, h: 190, seg: 16 }, {
        mat: 'rubber',
        x: -650,
        z: -330,
        y: 800,
        pattern: { kind: 'linear', count: 2, spacing: 1300, axis: 'x', count2: 2, spacing2: 660, axis2: 'z' },
      }),
      ft('box', 'Trough floor', { w: 1900, h: 25, d: 700 }, { mat: 'stainless', y: 990 }),
      ft('box', 'Side walls', { w: 1900, h: 260, d: 25 }, {
        mat: 'stainless',
        y: 1015,
        z: -337,
        pattern: { kind: 'linear', count: 2, spacing: 674, axis: 'z' },
      }),
      ft('box', 'Back plate', { w: 25, h: 300, d: 700 }, { mat: 'stainless', x: -950, y: 1015 }),
      ft('cyl', 'Drive unit', { r: 175, h: 420, seg: 24 }, { mat: 'motor', x: 700, y: 700, rz: 90 }),
      ft('box', 'Drive bracket', { w: 300, h: 240, d: 300 }, { mat: 'painted', x: 640, y: 760 }),
      ft('loft', 'Discharge lip', { w0: 420, d0: 700, w1: 700, d1: 700, h: 220 }, {
        mat: 'stainless',
        x: 1120,
        y: 990,
        rz: 24,
      }),
    ],
  },
  {
    id: 'feeder-screw',
    label: 'Screw feeder',
    icon: '≋',
    blurb: 'Auger in a trough, with inlet hopper, drive and outlet',
    build: () => [
      ft('box', 'Base frame', { w: 2400, h: 120, d: 420 }, { mat: 'column', y: 600 }),
      legs(2100, 300, 600, 90, 'column', 'Stand'),
      ft('cyl', 'Trough tube', { r: 240, h: 2100, seg: 30 }, { mat: 'galv', x: -1000, y: 960, rz: -90 }),
      ft('cyl', 'Screw shaft', { r: 55, h: 2000, seg: 18 }, { mat: 'machine', x: -950, y: 960, rz: -90 }),
      ft('helix', 'Screw flight', { rIn: 55, rOut: 205, pitch: 360, turns: 5.2, t: 9 }, {
        mat: 'stainless',
        x: -900,
        y: 960,
        rz: -90,
      }),
      ft('loft', 'Inlet hopper', { w0: 500, d0: 420, w1: 1000, d1: 900, h: 700 }, {
        mat: 'galv',
        x: -600,
        y: 1200,
      }),
      ft('pipe', 'Outlet spout', { r: 190, len: 420, flange: 1, seg: 24 }, { x: 900, y: 460 }),
      ft('box', 'Gearbox', { w: 340, h: 380, d: 340 }, { mat: 'painted', x: 1300, y: 780 }),
      ft('cyl', 'Motor', { r: 145, h: 460, seg: 22 }, { mat: 'motor', x: 1720, y: 960, rz: -90 }),
      ft('box', 'End bearing', { w: 160, h: 220, d: 240 }, { mat: 'cast', x: -1180, y: 850 }),
    ],
  },
  {
    id: 'silo',
    label: 'Silo',
    icon: '⬯',
    blurb: 'Cylindrical silo on a skirt, conical discharge and roof',
    build: () => [
      ft('cyl', 'Support skirt', { r: 1500, h: 2600, seg: 36 }, { mat: 'column' }),
      ft('cone', 'Discharge cone', { r0: 220, r1: 1500, h: 1600, seg: 36 }, { mat: 'galv', y: 1100 }),
      ft('cyl', 'Shell', { r: 1500, h: 5200, seg: 36 }, { mat: 'steel', y: 2700 }),
      ft('ring', 'Shell stiffeners', { R: 1512, r: 40, seg: 40 }, {
        mat: 'steel',
        y: 3600,
        pattern: { kind: 'linear', count: 4, spacing: 1200, axis: 'y' },
      }),
      ft('cone', 'Roof', { r0: 1520, r1: 260, h: 900, seg: 36 }, { mat: 'galv', y: 7900 }),
      ft('cyl', 'Filter housing', { r: 260, h: 700, seg: 22 }, { mat: 'stainless', y: 8800 }),
      ft('pipe', 'Discharge', { r: 220, len: 500, flange: 1, seg: 24 }, { y: 600 }),
      ft('box', 'Fill pipe', { w: 180, h: 6600, d: 180 }, { mat: 'pipe', x: 1680, y: 2600 }),
    ],
  },
  {
    id: 'cyclone',
    label: 'Cyclone',
    icon: '◟',
    blurb: 'Barrel, cone, tangential inlet and vortex finder',
    build: () => [
      ft('cone', 'Cone', { r0: 130, r1: 620, h: 1900, seg: 32 }, { mat: 'stainless', y: 900 }),
      ft('cyl', 'Barrel', { r: 620, h: 1250, seg: 32 }, { mat: 'stainless', y: 2800 }),
      ft('box', 'Tangential inlet', { w: 900, h: 420, d: 260 }, { mat: 'stainless', x: 700, y: 3500, z: 480 }),
      ft('cyl', 'Vortex finder', { r: 300, h: 900, seg: 24 }, { mat: 'stainless', y: 3700 }),
      ft('cyl', 'Outlet duct', { r: 300, h: 700, seg: 24 }, { mat: 'galv', y: 4050 }),
      ft('ring', 'Barrel flange', { R: 630, r: 30, seg: 36 }, { mat: 'steel', y: 2800 }),
      ft('pipe', 'Dust outlet', { r: 130, len: 400, flange: 1, seg: 20 }, { y: 500 }),
      ft('box', 'Support legs', { w: 90, h: 900, d: 90 }, {
        mat: 'column',
        pattern: { kind: 'circular', count: 4, spacing: 560, axis: 'y', sweep: 360 },
      }),
    ],
  },
  {
    id: 'chute',
    label: 'Chute',
    icon: '⟋',
    blurb: 'Sloped rectangular chute with flanged ends and a liner',
    build: () => [
      ft('box', 'Chute body', { w: 480, h: 2600, d: 380 }, { mat: 'galv', y: 300, rz: 32 }),
      ft('box', 'Liner', { w: 440, h: 2560, d: 40 }, { mat: 'rubber', y: 320, z: -160, rz: 32 }),
      ft('box', 'Top flange', { w: 620, h: 40, d: 520 }, { mat: 'steel', x: -690, y: 2510, rz: 32 }),
      ft('box', 'Bottom flange', { w: 620, h: 40, d: 520 }, { mat: 'steel', x: 690, y: 300, rz: 32 }),
      ft('box', 'Inspection door', { w: 320, h: 30, d: 300 }, { mat: 'machine', x: 260, y: 1180, z: 195, rz: 32 }),
      ft('box', 'Support bracket', { w: 120, h: 900, d: 120 }, { mat: 'column', x: 620, y: 0 }),
    ],
  },
  {
    id: 'conveyor',
    label: 'Belt conveyor',
    icon: '⇥',
    blurb: 'Belt bed on idlers, drive and tail pulleys, skirt plates',
    build: () => [
      legs(3200, 700, 900, 100, 'column', 'Stringer legs'),
      ft('box', 'Stringers', { w: 4200, h: 160, d: 90 }, {
        mat: 'column',
        y: 900,
        z: -400,
        pattern: { kind: 'linear', count: 2, spacing: 800, axis: 'z' },
      }),
      ft('cyl', 'Idlers', { r: 70, h: 780, seg: 18 }, {
        mat: 'machine',
        x: -1900,
        y: 1090,
        z: -390,
        rx: 90,
        pattern: { kind: 'linear', count: 9, spacing: 480, axis: 'x' },
      }),
      ft('box', 'Belt', { w: 4300, h: 18, d: 700 }, { mat: 'rubber', y: 1160 }),
      ft('box', 'Skirt plates', { w: 3400, h: 220, d: 20 }, {
        mat: 'galv',
        y: 1178,
        z: -350,
        pattern: { kind: 'linear', count: 2, spacing: 700, axis: 'z' },
      }),
      ft('cyl', 'Drive pulley', { r: 160, h: 800, seg: 24 }, { mat: 'machine', x: 2150, y: 1010, z: -400, rx: 90 }),
      ft('cyl', 'Tail pulley', { r: 130, h: 800, seg: 24 }, { mat: 'machine', x: -2150, y: 1040, z: -400, rx: 90 }),
      ft('cyl', 'Drive motor', { r: 140, h: 420, seg: 20 }, { mat: 'motor', x: 2150, y: 740, z: 620, rz: -90 }),
      ft('box', 'Gearbox', { w: 300, h: 300, d: 300 }, { mat: 'painted', x: 2150, y: 640, z: 300 }),
    ],
  },
  {
    id: 'tank',
    label: 'Tank',
    icon: '⬭',
    blurb: 'Dished-end vessel with nozzles on a bolt circle and a skirt',
    build: () => [
      ft('cyl', 'Skirt', { r: 850, h: 900, seg: 32 }, { mat: 'column' }),
      ft('vessel', 'Shell', { r: 850, h: 2200, endBot: 1, endTop: 1, coneH: 800, seg: 36 }, {
        mat: 'stainless',
        y: 900,
      }),
      // Seated ON the dished head: at a 480 bolt circle a 2:1 head of radius
      // 850 stands about 3875 high, and the crown is at 3950.
      ft('pipe', 'Top nozzles', { r: 70, len: 260, flange: 1, seg: 18 }, {
        mat: 'stainless',
        x: 480,
        y: 3850,
        pattern: { kind: 'circular', count: 4, spacing: 480, axis: 'y', sweep: 360 },
      }),
      ft('pipe', 'Manway', { r: 250, len: 300, flange: 1, seg: 26 }, { mat: 'stainless', y: 3920 }),
      ft('pipe', 'Drain', { r: 80, len: 400, flange: 1, seg: 18 }, { mat: 'stainless', y: 500 }),
      ft('box', 'Sight glass', { w: 90, h: 900, d: 40 }, { mat: 'glass', x: 880, y: 1600 }),
      ft('ring', 'Lifting lugs', { R: 880, r: 40, seg: 8 }, { mat: 'steel', y: 3450 }),
    ],
  },
  {
    id: 'stand',
    label: 'Support stand',
    icon: '⌗',
    blurb: 'Bolted frame: columns, beams, bracing and base plates',
    build: () => [
      ft('box', 'Base plates', { w: 260, h: 25, d: 260 }, {
        mat: 'steel',
        x: -900,
        z: -700,
        pattern: { kind: 'linear', count: 2, spacing: 1800, axis: 'x', count2: 2, spacing2: 1400, axis2: 'z' },
      }),
      ft('box', 'Columns', { w: 120, h: 2400, d: 120 }, {
        mat: 'column',
        x: -900,
        y: 25,
        z: -700,
        pattern: { kind: 'linear', count: 2, spacing: 1800, axis: 'x', count2: 2, spacing2: 1400, axis2: 'z' },
      }),
      ft('box', 'Top beams (X)', { w: 1920, h: 180, d: 100 }, {
        mat: 'column',
        y: 2425,
        z: -700,
        pattern: { kind: 'linear', count: 2, spacing: 1400, axis: 'z' },
      }),
      ft('box', 'Top beams (Z)', { w: 100, h: 180, d: 1520 }, {
        mat: 'column',
        x: -900,
        y: 2425,
        pattern: { kind: 'linear', count: 2, spacing: 1800, axis: 'x' },
      }),
      ft('box', 'Mid rails', { w: 1920, h: 120, d: 80 }, {
        mat: 'column',
        y: 1150,
        z: -700,
        pattern: { kind: 'linear', count: 2, spacing: 1400, axis: 'z' },
      }),
      ft('box', 'Bracing', { w: 90, h: 2200, d: 60 }, { mat: 'steel', x: -900, y: 100, z: -700, rz: -38 }),
      ft('box', 'Deck', { w: 2000, h: 30, d: 1600 }, { mat: 'deck', y: 2605 }),
    ],
  },
  {
    id: 'rotary-valve',
    label: 'Rotary valve',
    icon: '⊛',
    blurb: 'Rotor housing with end covers, drive and square flanges',
    build: () => [
      ft('cyl', 'Housing', { r: 300, h: 460, seg: 30 }, { mat: 'cast', y: 400, rz: 90, x: 230 }),
      ft('box', 'Inlet flange', { w: 480, h: 40, d: 480 }, { mat: 'machine', y: 690 }),
      ft('box', 'Outlet flange', { w: 480, h: 40, d: 480 }, { mat: 'machine', y: 70 }),
      ft('cyl', 'End covers', { r: 320, h: 35, seg: 30 }, {
        mat: 'machine',
        x: -230,
        y: 400,
        rz: 90,
        pattern: { kind: 'linear', count: 2, spacing: 495, axis: 'x' },
      }),
      ft('box', 'Rotor blades', { w: 280, h: 30, d: 440 }, {
        mat: 'stainless',
        y: 400,
        pattern: { kind: 'circular', count: 8, spacing: 1, axis: 'y', sweep: 360 },
      }),
      ft('cyl', 'Drive shaft', { r: 45, h: 320, seg: 16 }, { mat: 'machine', x: 300, y: 400, rz: -90 }),
      ft('cyl', 'Motor', { r: 130, h: 380, seg: 20 }, { mat: 'motor', x: 620, y: 400, rz: -90 }),
      ft('box', 'Gearbox', { w: 260, h: 260, d: 260 }, { mat: 'painted', x: 430, y: 270 }),
    ],
  },
  {
    id: 'bin',
    label: 'Bin',
    icon: '▤',
    blurb: 'Square bin with a hopper bottom, on a bolted frame',
    build: () => [
      legs(1600, 1600, 1400, 110, 'column', 'Frame legs'),
      ft('box', 'Frame rails', { w: 1760, h: 130, d: 110 }, {
        mat: 'column',
        y: 1400,
        z: -800,
        pattern: { kind: 'linear', count: 2, spacing: 1600, axis: 'z' },
      }),
      ft('loft', 'Hopper section', { w0: 350, d0: 350, w1: 1800, d1: 1800, h: 1000 }, { y: 1530 }),
      ft('box', 'Bin walls', { w: 1800, h: 1800, d: 1800 }, { mat: 'galv', y: 2530 }),
      ft('box', 'Top cover', { w: 1880, h: 60, d: 1880 }, { mat: 'steel', y: 4330 }),
      ft('pipe', 'Vent', { r: 110, len: 500, flange: 0, seg: 18 }, { mat: 'galv', x: 600, y: 4390 }),
      ft('pipe', 'Outlet', { r: 175, len: 340, flange: 1, seg: 22 }, { y: 1200 }),
      ft('box', 'Level probes', { w: 60, h: 500, d: 60 }, {
        mat: 'machine',
        x: -600,
        y: 3800,
        pattern: { kind: 'linear', count: 2, spacing: 400, axis: 'y' },
      }),
    ],
  },
  {
    id: 'elevator',
    label: 'Bucket elevator',
    icon: '⇧',
    blurb: 'Boot, twin casings, head section, drive and chutes',
    build: () => [
      ft('box', 'Boot section', { w: 900, h: 900, d: 620 }, { mat: 'galv' }),
      ft('box', 'Casings', { w: 520, h: 6200, d: 520 }, {
        mat: 'galv',
        x: -330,
        y: 900,
        pattern: { kind: 'linear', count: 2, spacing: 660, axis: 'x' },
      }),
      ft('box', 'Head section', { w: 1500, h: 1000, d: 820 }, { mat: 'galv', y: 7100 }),
      ft('cyl', 'Head pulley', { r: 250, h: 540, seg: 24 }, { mat: 'machine', y: 7600, z: -270, rx: 90 }),
      ft('cyl', 'Boot pulley', { r: 200, h: 540, seg: 24 }, { mat: 'machine', y: 430, z: -270, rx: 90 }),
      ft('box', 'Gearbox', { w: 420, h: 440, d: 420 }, { mat: 'painted', x: 1020, y: 7280 }),
      ft('cyl', 'Drive motor', { r: 165, h: 540, seg: 22 }, { mat: 'motor', x: 1280, y: 7500, rz: -90 }),
      ft('loft', 'Inlet chute', { w0: 420, d0: 420, w1: 720, d1: 620, h: 620 }, {
        mat: 'galv',
        x: -880,
        y: 900,
      }),
      ft('box', 'Discharge chute', { w: 900, h: 720, d: 620 }, { mat: 'galv', x: 1120, y: 6650, rz: 28 }),
      ft('box', 'Base plinth', { w: 1200, h: 200, d: 900 }, { mat: 'concrete', y: -0 }),
    ],
  },
  {
    id: 'bagfilter',
    label: 'Bag filter',
    icon: '▥',
    blurb: 'Dust collector: hopper, housing, plenum, fan and side inlet',
    build: () => [
      legs(1400, 1000, 1500, 100, 'column', 'Legs'),
      ft('loft', 'Dust hopper', { w0: 300, d0: 300, w1: 1500, d1: 1100, h: 800 }, {
        mat: 'galv',
        y: 1500,
      }),
      ft('box', 'Filter housing', { w: 1500, h: 2000, d: 1100 }, { mat: 'painted', y: 2300 }),
      ft('box', 'Clean air plenum', { w: 1580, h: 620, d: 1180 }, { mat: 'painted', y: 4300 }),
      ft('box', 'Access doors', { w: 40, h: 620, d: 900 }, {
        mat: 'machine',
        x: 770,
        y: 2620,
        pattern: { kind: 'linear', count: 2, spacing: 760, axis: 'y' },
      }),
      // A square housing branch onto a round duct — the fitting a cone cannot make.
      ft('sqround', 'Inlet transition', { w: 700, d: 520, r: 220, h: 480, flip: 0, seg: 28 }, {
        mat: 'galv',
        x: -1010,
        y: 2700,
        rz: -90,
      }),
      ft('pipe', 'Outlet duct', { r: 220, len: 900, flange: 1, seg: 24 }, { mat: 'galv', y: 4920 }),
      ft('cyl', 'Fan housing', { r: 430, h: 420, seg: 30 }, { mat: 'painted', x: 1320, y: 4700, rz: -90 }),
      ft('cyl', 'Fan motor', { r: 175, h: 520, seg: 22 }, { mat: 'motor', x: 1960, y: 4700, rz: -90 }),
      ft('pipe', 'Rotary valve outlet', { r: 150, len: 420, flange: 1, seg: 22 }, { y: 1080 }),
    ],
  },
  {
    id: 'diverter',
    label: 'Diverter chute',
    icon: 'Y',
    blurb: 'Two-way chute with flap and actuator, on a frame',
    build: () => [
      ft('box', 'Support frame', { w: 110, h: 700, d: 110 }, {
        mat: 'column',
        x: -450,
        z: -250,
        pattern: { kind: 'linear', count: 2, spacing: 900, axis: 'x', count2: 2, spacing2: 500, axis2: 'z' },
      }),
      ft('box', 'Left outlet', { w: 420, h: 900, d: 460 }, { mat: 'stainless', x: -520, y: 700, rz: -24 }),
      ft('box', 'Right outlet', { w: 420, h: 900, d: 460 }, { mat: 'stainless', x: 520, y: 700, rz: 24 }),
      ft('loft', 'Diverter body', { w0: 1000, d0: 500, w1: 500, d1: 500, h: 600 }, {
        mat: 'stainless',
        y: 1400,
      }),
      ft('box', 'Flap', { w: 30, h: 560, d: 440 }, { mat: 'machine', y: 1440, rz: 18 }),
      ft('sqround', 'Inlet spool', { w: 500, d: 500, r: 200, h: 420, flip: 1, seg: 28 }, {
        mat: 'stainless',
        y: 2000,
      }),
      ft('box', 'Actuator bracket', { w: 220, h: 170, d: 220 }, { mat: 'painted', x: 640, y: 1680 }),
      ft('cyl', 'Actuator', { r: 80, h: 540, seg: 18 }, { mat: 'machine', x: 700, y: 1850, rz: -40 }),
    ],
  },
  {
    id: 'activator',
    label: 'Bin activator',
    icon: '⌄',
    blurb: 'Vibrating discharger: cone, gyrator, isolators and sleeve',
    build: () => [
      ft('box', 'Support legs', { w: 100, h: 600, d: 100 }, {
        mat: 'column',
        pattern: { kind: 'circular', count: 3, spacing: 820, axis: 'y', sweep: 360 },
      }),
      ft('cone', 'Activator cone', { r0: 220, r1: 700, h: 700, seg: 32 }, { mat: 'stainless', y: 600 }),
      ft('ring', 'Top flange', { R: 710, r: 30, seg: 36 }, { mat: 'steel', y: 1300 }),
      ft('cyl', 'Gyrator motor', { r: 170, h: 380, seg: 22 }, { mat: 'motor', x: 830, y: 900, rz: -90 }),
      ft('box', 'Motor bracket', { w: 300, h: 200, d: 260 }, { mat: 'painted', x: 760, y: 790 }),
      ft('cyl', 'Isolation mounts', { r: 60, h: 190, seg: 16 }, {
        mat: 'rubber',
        y: 1300,
        pattern: { kind: 'circular', count: 3, spacing: 800, axis: 'y', sweep: 360 },
      }),
      // Real wall thickness, because you look straight down into it.
      ft('shell', 'Flexible sleeve', { r: 230, wall: 14, h: 220, seg: 26 }, { mat: 'rubber', y: 1490 }),
      ft('pipe', 'Discharge spout', { r: 200, len: 420, flange: 1, seg: 24 }, { mat: 'stainless', y: 190 }),
    ],
  },
  {
    id: 'blender',
    label: 'Ribbon blender',
    icon: '⋃',
    blurb: 'U-trough with twin ribbons, covers, drive and discharge valve',
    build: () => [
      legs(2000, 700, 900, 100, 'column', 'Stand'),
      // The U trough is one closed cross-section, so it comes with end plates.
      ft('trough', 'Mixer trough', { r: 500, sideH: 420, wall: 8, len: 2600 }, {
        mat: 'stainless',
        x: -1300,
        y: 900,
      }),
      ft('cyl', 'Shaft', { r: 70, h: 2900, seg: 18 }, { mat: 'machine', x: -1450, y: 1400, rz: -90 }),
      ft('helix', 'Outer ribbon', { rIn: 70, rOut: 440, pitch: 900, turns: 2.6, t: 12 }, {
        mat: 'stainless',
        x: -1200,
        y: 1400,
        rz: -90,
      }),
      ft('helix', 'Inner ribbon', { rIn: 70, rOut: 260, pitch: 900, turns: 2.6, t: 12 }, {
        mat: 'stainless',
        x: -1200,
        y: 1400,
        ry: 180,
        rz: -90,
      }),
      ft('box', 'Trough covers', { w: 1280, h: 40, d: 1040 }, {
        mat: 'stainless',
        x: -650,
        y: 1820,
        pattern: { kind: 'linear', count: 2, spacing: 1300, axis: 'x' },
      }),
      ft('box', 'Gearbox', { w: 440, h: 470, d: 440 }, { mat: 'painted', x: 1620, y: 1160 }),
      ft('cyl', 'Drive motor', { r: 180, h: 560, seg: 22 }, { mat: 'motor', x: 2080, y: 1400, rz: -90 }),
      ft('box', 'Discharge valve', { w: 420, h: 260, d: 380 }, { mat: 'machine', y: 640 }),
      ft('pipe', 'Outlet', { r: 170, len: 360, flange: 1, seg: 22 }, { mat: 'stainless', y: 280 }),
    ],
  },
  {
    id: 'weighhopper',
    label: 'Weigh hopper',
    icon: '⚖',
    blurb: 'Hopper slung on load cells in a rolled-steel frame',
    build: () => [
      ft('section', 'Frame columns', { shape: 0, dep: 180, wid: 100, t: 8, len: 2600 }, {
        mat: 'column',
        x: -800,
        z: -700,
        pattern: { kind: 'linear', count: 2, spacing: 1600, axis: 'x', count2: 2, spacing2: 1400, axis2: 'z' },
      }),
      ft('section', 'Top beams', { shape: 1, dep: 160, wid: 80, t: 6, len: 1700 }, {
        mat: 'column',
        x: -850,
        y: 2660,
        z: -700,
        rz: -90,
        pattern: { kind: 'linear', count: 2, spacing: 1400, axis: 'z' },
      }),
      ft('cyl', 'Load cells', { r: 70, h: 200, seg: 18 }, {
        mat: 'machine',
        x: -800,
        y: 2740,
        z: -700,
        pattern: { kind: 'linear', count: 2, spacing: 1600, axis: 'x', count2: 2, spacing2: 1400, axis2: 'z' },
      }),
      ft('box', 'Hopper collar', { w: 1580, h: 80, d: 1380 }, { mat: 'steel', y: 2660 }),
      ft('loft', 'Weigh hopper', { w0: 350, d0: 350, w1: 1500, d1: 1300, h: 1060 }, {
        mat: 'stainless',
        y: 1600,
      }),
      ft('shell', 'Flexible connection', { r: 210, wall: 12, h: 260, seg: 26 }, { mat: 'rubber', y: 1340 }),
      ft('box', 'Slide gate', { w: 540, h: 40, d: 440 }, { mat: 'machine', y: 1300 }),
      ft('pipe', 'Discharge', { r: 180, len: 380, flange: 1, seg: 22 }, { mat: 'stainless', y: 920 }),
    ],
  },
  {
    id: 'platform',
    label: 'Access platform',
    icon: '⌸',
    blurb: 'Beams and columns, grating deck, handrail and access stair',
    build: () => [
      ft('section', 'Columns', { shape: 0, dep: 200, wid: 120, t: 9, len: 3000 }, {
        mat: 'column',
        x: -1200,
        z: -900,
        pattern: { kind: 'linear', count: 2, spacing: 2400, axis: 'x', count2: 2, spacing2: 1800, axis2: 'z' },
      }),
      ft('section', 'Edge beams (X)', { shape: 1, dep: 180, wid: 90, t: 7, len: 2520 }, {
        mat: 'column',
        x: -1260,
        y: 3060,
        z: -900,
        rz: -90,
        pattern: { kind: 'linear', count: 2, spacing: 1800, axis: 'z' },
      }),
      ft('section', 'Edge beams (Z)', { shape: 1, dep: 180, wid: 90, t: 7, len: 1920 }, {
        mat: 'column',
        x: -1200,
        y: 3060,
        z: -960,
        rx: 90,
        pattern: { kind: 'linear', count: 2, spacing: 2400, axis: 'x' },
      }),
      ft('box', 'Grating deck', { w: 2600, h: 35, d: 2000 }, { mat: 'grating', y: 3150 }),
      ft('box', 'Toe plates', { w: 2700, h: 120, d: 20 }, {
        mat: 'galv',
        y: 3185,
        z: -1000,
        pattern: { kind: 'linear', count: 2, spacing: 2000, axis: 'z' },
      }),
      ft('cyl', 'Handrail posts', { r: 25, h: 1100, seg: 12 }, {
        mat: 'galv',
        x: -1300,
        y: 3185,
        z: -1000,
        pattern: { kind: 'linear', count: 5, spacing: 650, axis: 'x', count2: 2, spacing2: 2000, axis2: 'z' },
      }),
      ft('cyl', 'Top rails', { r: 25, h: 2700, seg: 12 }, {
        mat: 'galv',
        x: -1350,
        y: 4260,
        z: -1000,
        rz: -90,
        pattern: { kind: 'linear', count: 2, spacing: 2000, axis: 'z' },
      }),
      ft('cyl', 'Mid rails', { r: 20, h: 2700, seg: 12 }, {
        mat: 'galv',
        x: -1350,
        y: 3760,
        z: -1000,
        rz: -90,
        pattern: { kind: 'linear', count: 2, spacing: 2000, axis: 'z' },
      }),
      // ── Stair up to the deck, rather than a caged ladder ──
      // A ladder is what you fit when there is no room for a stair; on an
      // access platform you walk up. The treads and risers are one sketched
      // profile because a stepped run is diagonal, and a pattern can only step
      // along an axis.
      ft('section', 'Stair stringers', { shape: 1, dep: 250, wid: 80, t: 8, len: 4760 }, {
        mat: 'column',
        x: 4836,
        // The channel profile is centred, so the pitched section's lower flange
        // corner would otherwise sit below the floor slab.
        y: 30,
        z: -460,
        rz: 48,
        pattern: { kind: 'linear', count: 2, spacing: 920, axis: 'z' },
      }),
      ft('sketch', 'Treads and risers', { h: 900 }, {
        mat: 'deck',
        x: 1300,
        y: 0,
        z: -450,
        sketch: { plane: 'xy', op: 'extrude', pts: STAIR_PROFILE },
      }),
      ft('cyl', 'Stair handrails', { r: 25, h: 4760, seg: 12 }, {
        mat: 'galv',
        x: 4836,
        y: 1000,
        z: -460,
        rz: 48,
        pattern: { kind: 'linear', count: 2, spacing: 920, axis: 'z' },
      }),
      ft('cyl', 'Stair knee rails', { r: 20, h: 4760, seg: 12 }, {
        mat: 'galv',
        x: 4836,
        y: 500,
        z: -460,
        rz: 48,
        pattern: { kind: 'linear', count: 2, spacing: 920, axis: 'z' },
      }),
    ],
  },
  {
    id: 'bigbag',
    label: 'Big-bag station',
    icon: '⬓',
    blurb: 'Discharge frame, bag hooks, massagers and hopper',
    build: () => [
      ft('section', 'Frame columns', { shape: 3, dep: 120, wid: 120, t: 8, len: 3400 }, {
        mat: 'column',
        x: -800,
        z: -800,
        pattern: { kind: 'linear', count: 2, spacing: 1600, axis: 'x', count2: 2, spacing2: 1600, axis2: 'z' },
      }),
      ft('section', 'Top frame', { shape: 3, dep: 120, wid: 120, t: 8, len: 1720 }, {
        mat: 'column',
        x: -860,
        y: 3460,
        z: -800,
        rz: -90,
        pattern: { kind: 'linear', count: 2, spacing: 1600, axis: 'z' },
      }),
      ft('box', 'Lifting cross', { w: 1500, h: 90, d: 90 }, { mat: 'steel', y: 3300 }),
      ft('cyl', 'Bag hooks', { r: 30, h: 270, seg: 12 }, {
        mat: 'machine',
        x: -500,
        y: 3040,
        z: -500,
        pattern: { kind: 'linear', count: 2, spacing: 1000, axis: 'x', count2: 2, spacing2: 1000, axis2: 'z' },
      }),
      ft('loft', 'Bag', { w0: 900, d0: 900, w1: 1060, d1: 1060, h: 1420 }, { mat: 'cardboard', y: 1600 }),
      ft('box', 'Massage paddles', { w: 120, h: 620, d: 420 }, {
        mat: 'machine',
        x: -700,
        y: 1900,
        pattern: { kind: 'linear', count: 2, spacing: 1400, axis: 'x' },
      }),
      ft('box', 'Dust seal', { w: 540, h: 130, d: 540 }, { mat: 'rubber', y: 1470 }),
      ft('loft', 'Discharge hopper', { w0: 320, d0: 320, w1: 1200, d1: 1200, h: 780 }, {
        mat: 'stainless',
        y: 700,
      }),
      ft('pipe', 'Outlet', { r: 180, len: 400, flange: 1, seg: 22 }, { mat: 'stainless', y: 320 }),
    ],
  },
  {
    id: 'drum',
    label: 'Rotary drum',
    icon: '⬬',
    blurb: 'Drum on trunnions with riding rings, girth gear and hoods',
    build: () => [
      ft('box', 'Trunnion bases', { w: 700, h: 160, d: 1700 }, {
        mat: 'concrete',
        x: -1800,
        pattern: { kind: 'linear', count: 2, spacing: 3600, axis: 'x' },
      }),
      ft('cyl', 'Trunnion rollers', { r: 280, h: 520, seg: 24 }, {
        mat: 'machine',
        x: -1800,
        y: 440,
        z: -700,
        rx: 90,
        pattern: { kind: 'linear', count: 2, spacing: 3600, axis: 'x', count2: 2, spacing2: 1400, axis2: 'z' },
      }),
      ft('cyl', 'Drum shell', { r: 900, h: 6000, seg: 36 }, { mat: 'steel', x: -3000, y: 1420, rz: -90 }),
      ft('ring', 'Riding rings', { R: 960, r: 70, seg: 36 }, {
        mat: 'steel',
        x: -1800,
        y: 1420,
        rz: -90,
        pattern: { kind: 'linear', count: 2, spacing: 3600, axis: 'x' },
      }),
      ft('ring', 'Girth gear', { R: 980, r: 60, seg: 40 }, { mat: 'cast', x: 700, y: 1420, rz: -90 }),
      ft('box', 'Feed hood', { w: 700, h: 1500, d: 1500 }, { mat: 'galv', x: -3320, y: 670 }),
      ft('box', 'Discharge hood', { w: 700, h: 1500, d: 1500 }, { mat: 'galv', x: 3320, y: 670 }),
      ft('box', 'Gearbox', { w: 520, h: 520, d: 520 }, { mat: 'painted', x: 700, y: 180, z: 1050 }),
      ft('cyl', 'Drive motor', { r: 200, h: 620, seg: 22 }, { mat: 'motor', x: 700, y: 440, z: 1500, rz: -90 }),
    ],
  },
  {
    id: 'hood',
    label: 'Extraction hood',
    icon: '⌂',
    blurb: 'Capture hood, square-to-round transition and ducted run',
    build: () => [
      ft('box', 'Support posts', { w: 80, h: 1200, d: 80 }, {
        mat: 'column',
        x: -600,
        z: -400,
        pattern: { kind: 'linear', count: 2, spacing: 1200, axis: 'x', count2: 2, spacing2: 800, axis2: 'z' },
      }),
      ft('box', 'Capture skirt', { w: 1520, h: 200, d: 1120 }, { mat: 'rubber', y: 1000 }),
      ft('loft', 'Hood', { w0: 1400, d0: 1000, w1: 500, d1: 500, h: 800 }, { mat: 'galv', y: 1200 }),
      // The fitting between a square hood and a round duct.
      ft('sqround', 'Transition', { w: 500, d: 500, r: 180, h: 420, flip: 0, seg: 28 }, {
        mat: 'galv',
        y: 2000,
      }),
      ft('pipe', 'Riser', { r: 180, len: 880, flange: 1, seg: 24 }, { mat: 'galv', y: 2420 }),
      ft('elbow', 'Duct bend', { R: 400, r: 180, sweep: 90, seg: 22 }, { mat: 'galv', y: 3300 }),
      ft('pipe', 'Horizontal run', { r: 180, len: 1500, flange: 0, seg: 24 }, {
        mat: 'galv',
        x: -400,
        y: 3700,
        rz: 90,
      }),
      ft('box', 'Duct hangers', { w: 60, h: 500, d: 60 }, {
        mat: 'galv',
        x: -900,
        y: 3880,
        pattern: { kind: 'linear', count: 2, spacing: 600, axis: 'x' },
      }),
    ],
  },
];

/** Look a template up by id. */
export const templateById = (id: string): Template | undefined => TEMPLATES.find((t) => t.id === id);

/** A whole part from a template, named after it. */
export function partFromTemplate(id: string): Part | null {
  const t = templateById(id);
  if (!t) return null;
  return { name: t.label, mmPerUnit: 25, features: t.build() };
}
